import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  first,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { getIsNotNullable } from 'src/app/core/nullable-utils';
import { DeliverableService } from 'src/app/core/services/deliverable-services/deliverable.service';
import { LoaderComponent } from 'src/app/shared/loading/loader/loader.component';
import { DEFAULT_DELAY_MS } from 'src/constants';
import { ProjectService } from '../../../core/services/project.service';

interface KeyValuePair {
  key: string;
  value: string;
}

@Component({
  selector: 'app-deliverable-search',
  imports: [
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatIconModule,
    MatBadgeModule,
    ReactiveFormsModule,
    LoaderComponent,
  ],
  templateUrl: './deliverable-search.component.html',
  styleUrl: './deliverable-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliverableSearchComponent {
  readonly #deliverableService = inject(DeliverableService);
  readonly #projectService = inject(ProjectService);
  readonly #destroyRef = inject(DestroyRef);
  readonly searchInput = viewChild<ElementRef>('searchInput');
  readonly select = output<number>();
  searchControl = new FormControl('');
  readonly options = signal<KeyValuePair[]>([]);
  readonly projectId = this.#projectService.currentProjectId;
  readonly #projectId$ = toObservable(this.projectId);
  readonly isSearching = signal(false);
  readonly isSelectingOption = signal(false);

  constructor() {
    this.#setupSearch()
      .pipe(map(formatOptions))
      .subscribe(response => this.options.set(response));
  }

  onSelected(event: MatAutocompleteSelectedEvent) {
    this.isSelectingOption.set(true);
    this.#clearSearch();
    this.select.emit(Number(event.option.value));
  }

  #search(query: string) {
    if (this.isSelectingOption()) {
      this.isSelectingOption.set(false);
      this.isSearching.set(false);
      return of([]);
    }

    return this.#projectId$.pipe(
      filter(getIsNotNullable),
      switchMap(projectId =>
        this.#deliverableService.searchDeliverables(projectId, query).pipe(
          catchError(error => {
            console.error('Error searching deliverables:', error);
            return of([]);
          }),
          finalize(() => this.isSearching.set(false)),
        ),
      ),
      first(),
    );
  }
  #setupSearch() {
    return this.searchControl.valueChanges.pipe(
      map(query => query || ''),
      filter(query => query.trim().length > 0),
      tap(() => this.isSearching.set(true)),
      debounceTime(DEFAULT_DELAY_MS),
      distinctUntilChanged(),
      switchMap(query => this.#search(query)),
      takeUntilDestroyed(this.#destroyRef),
    );
  }

  #clearSearch() {
    this.searchControl.setValue('');
    this.options.set([]);
    this.isSearching.set(false);
  }
}

function formatOptions(deliverables: Record<string, number> | never[]): KeyValuePair[] {
  if (Array.isArray(deliverables)) {
    return [];
  }

  return Object.keys(deliverables).map(dvName => ({ key: deliverables[dvName].toString(), value: dvName }));
}
